<template>
  <Transition name="fade">
    <Dialog
      :title="modalTitle"
      :show-cancel-button="false"
      :show-save-button="false"
      :custom-footer="true"
      :show-footer="true"
      :show-close-button="true"
      :sm="true"
      @closeEvent="closeEvent"
    >
      <template #modal-content>
        <!-- Shipping Date Section -->
        <div v-if="showShippingDate" class="mb-6">
          <h3 class="text-lg font-semibold mb-4">Estimated Shipping Date</h3>
          <Datepicker
            class="w-full"
            v-model="est_shipping_date"
            :min-date="new Date()"
            format="dd/MM/yyyy"
            placeholder="Choose a date"
          />
        </div>

        <!-- Put In Production Section -->
        <div v-if="showPutInProduction" class="mb-6">
          <h3 class="text-lg font-semibold mb-4">Put Order In Production</h3>
          <div class="mb-3">
            <label class="block text-sm text-gray-600 mb-1">Estimated Shipping Date</label>
            <Datepicker
              v-model="est_shipping_date"
              :min-date="new Date()"
              format="dd/MM/yyyy"
              placeholder="Choose a date"
            />
          </div>
        </div>

        <!-- Shipping Details Section -->
        <div v-if="showShippingDetails" class="mb-6">
          <!-- Show existing shipping details if available -->
          <div
            v-if="orderItem.tracking_no && !editShippingLink"
            class="mb-4 p-3 bg-gray-50 rounded-md"
          >
            <div class="text-sm text-gray-600 mb-2">Current Shipping Details:</div>
            <div class="font-medium">{{ orderItem.shipping_company }}</div>
            <div>
              <a
                v-if="orderItem.tracking_link"
                :href="orderItem.tracking_link"
                target="_blank"
                class="text-blue-600 hover:underline"
              >
                {{ orderItem.tracking_no }}
              </a>
              <span v-else>{{ orderItem.tracking_no }}</span>
            </div>
          </div>

          <!-- Shipping Details Form -->
          <div v-if="!orderItem.tracking_no || editShippingLink" class="space-y-3">
            <div>
              <label class="block text-sm text-gray-600 mb-1">Shipping Company</label>
              <select
                v-model="shipping_company"
                @change="setTrackingLink(($event.target as HTMLSelectElement).value, 'company')"
                class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select shipping company</option>
                <option v-for="option in shippingOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm text-gray-600 mb-1">Tracking Number</label>
              <input
                v-model="tracking_no"
                @input="setTrackingLink(($event.target as HTMLInputElement).value, 'number')"
                type="text"
                placeholder="Enter tracking number"
                class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div v-if="editShippingLink || showShippingDetails">
              <label class="block text-sm text-gray-600 mb-1"
                >Tracking Link (Auto-generated or manual)</label
              >
              <input
                v-model="tracking_link"
                type="url"
                placeholder="Will be auto-generated when company and tracking number are entered"
                class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p v-if="tracking_link" class="text-xs text-gray-500 mt-1">
                Auto-generated tracking link. You can edit this manually if needed.
              </p>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <!-- Shipping Date Footer -->
        <div v-if="showShippingDate" class="flex gap-2 justify-end">
          <button
            @click="saveShippingDate(true)"
            class="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
          >
            Save and notify
          </button>
          <button
            @click="saveShippingDate(false)"
            class="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
          >
            Save without notifying
          </button>
          <button @click="closeEvent" class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md">
            Cancel
          </button>
        </div>

        <!-- Put In Production Footer -->
        <div v-if="showPutInProduction" class="flex gap-2 justify-end">
          <button
            @click="savePutInProduction(true)"
            class="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
          >
            Save and notify
          </button>
          <button
            @click="savePutInProduction(false)"
            class="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
          >
            Save without notifying
          </button>
          <button @click="closeEvent" class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md">
            Cancel
          </button>
        </div>

        <!-- Shipping Details Footer -->
        <div v-if="showShippingDetails" class="flex gap-2 justify-end">
          <button
            v-if="!orderItem.tracking_no || editShippingLink"
            @click="saveShippingDetails"
            class="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
          >
            {{ !editShippingLink ? 'Save' : 'Update' }}
          </button>
          <button @click="closeEvent" class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md">
            {{ orderItem.tracking_no ? 'Close' : 'Cancel' }}
          </button>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>
<script setup lang="ts">
import Datepicker from '@/components/Shared/Datepicker.vue';
import Dialog from '@/components/Shared/Dialog.vue';
import { showAlert, showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { OrderDetailItem } from '@/models';
import { format, isBefore, isValid, parseISO, startOfDay } from 'date-fns';
import { computed, ref } from 'vue';
interface Props {
  showShippingDate?: boolean;
  showPutInProduction?: boolean;
  showShippingDetails?: boolean;
  orderItem?: OrderDetailItem;
  order?: any;
  status?: string;
  est_shipping_date?: Date | string | null;
  editShippingLink?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showShippingDate: false,
  showPutInProduction: false,
  showShippingDetails: false,
  orderItem: null,
  order: null,
  status: '',
  est_shipping_date: null,
  editShippingLink: false,
});

const emit = defineEmits<{
  close: [];
  shippingDateSaved: [notify: boolean];
  putInProductionSaved: [notify: boolean];
  shippingDetailsSaved: [details: any];
}>();

const est_shipping_date = ref<Date | string | null>(props.est_shipping_date);
const shipping_dates = ref<string[]>([]);
const shipping_company = ref<string>(props.orderItem.shipping_company?.toLowerCase());
const tracking_no = ref<string>(props.orderItem.tracking_no);
const tracking_link = ref<string>(props.orderItem.tracking_link);

const shippingOptions = ref([
  { value: 'fedex', text: 'FedEx' },
  { value: 'ups', text: 'UPS' },
  { value: 'usps', text: 'USPS' },
  { value: 'dhl', text: 'DHL' },
]);

// Shipping companies with tracking link templates
const shipping_companies = ref({
  fedex: {
    name: 'FedEx',
    tracking_link: 'https://www.fedex.com/fedextrack/?trknbr={tracking_no}',
  },
  ups: {
    name: 'UPS',
    tracking_link: 'https://www.ups.com/track?tracknum={tracking_no}',
  },
  usps: {
    name: 'USPS',
    tracking_link: 'https://tools.usps.com/go/TrackConfirmAction?tLabels={tracking_no}',
  },
  dhl: {
    name: 'DHL',
    tracking_link: 'https://www.dhl.com/en/express/tracking.html?AWB={tracking_no}',
  },
});

const setTrackingLink = (value: string, type: 'company' | 'number') => {
  let company = null;

  if (type === 'company') {
    company = value;
    shipping_company.value = value;
  } else if (type === 'number') {
    company = shipping_company.value;
    tracking_no.value = value;
  }

  if (company && shipping_companies.value[company] && tracking_no.value) {
    let track_link = shipping_companies.value[company].tracking_link;
    track_link = track_link.replace('{tracking_no}', tracking_no.value);
    tracking_link.value = track_link;
  } else {
    tracking_link.value = '';
  }
};

const modalTitle = computed(() => {
  if (props.showShippingDate) {
    return 'Add Shipping Date';
  }
  if (props.showPutInProduction) {
    return 'Put Order In Production';
  }
  if (props.showShippingDetails) {
    return 'Enter Shipping Details';
  }
  return 'Shipping Details';
});

const closeEvent = () => {
  emit('close');
};

const saveShippingDate = async (notify: boolean) => {
  if (!est_shipping_date.value) {
    showAlert('ERROR', 'Please select a shipping date', 'error');
    return;
  }

  // Convert Date object to YYYY-MM-DD string format using date-fns
  let formattedDate: string;
  const dateValue = est_shipping_date.value;

  if (dateValue instanceof Date) {
    formattedDate = format(dateValue, 'yyyy-MM-dd');
  } else if (typeof dateValue === 'string') {
    // If it's already a string, ensure it's in the correct format
    const parsedDate = parseISO(dateValue);
    if (!isValid(parsedDate)) {
      showAlert('ERROR', 'Invalid date format', 'error');
      return;
    }
    formattedDate = format(parsedDate, 'yyyy-MM-dd');
  } else {
    showAlert('ERROR', 'Invalid date value', 'error');
    return;
  }

  // Validate that the date is not in the past using date-fns
  const estShippingDate = parseISO(formattedDate);
  const today = startOfDay(new Date());

  if (isBefore(estShippingDate, today)) {
    showAlert('ERROR', 'Estimated shipping date cannot be in the past.', 'error');
    return;
  }

  shipping_dates.value = [...shipping_dates.value, formattedDate];

  let url = `customer-orders/${props.orderItem.id}/est-shipping-date`;

  try {
    await http.post(url, { shipping_dates: shipping_dates.value });

    if (notify) {
      let url = `notify-customer-shipping-date/${props.orderItem.id}/order/${props.order.id}`;
      if (formattedDate) {
        await http.post(url);
        showAlert('Success', 'Shipping date added and customer notified', 'success');
      } else {
        showAlert('error', 'Please save shipping date.', 'error');
      }
    }

    est_shipping_date.value = null;
    showAlert('Success', 'Shipping date added', 'success');
    emit('shippingDateSaved', notify);
    closeEvent();
  } catch (errorResponse) {
    showAlert('ERROR', 'Failed to save shipping date', 'error');
  }
};

const savePutInProduction = async (notify: boolean) => {
  if (!est_shipping_date.value) {
    showAlert('ERROR', 'Please select a shipping date', 'error');
    return;
  }

  // Convert Date object to YYYY-MM-DD string format using date-fns
  let formattedDate: string;
  const dateValue = est_shipping_date.value;

  if (dateValue instanceof Date) {
    formattedDate = format(dateValue, 'yyyy-MM-dd');
  } else if (typeof dateValue === 'string') {
    // If it's already a string, ensure it's in the correct format
    const parsedDate = parseISO(dateValue);
    if (!isValid(parsedDate)) {
      showAlert('ERROR', 'Invalid date format', 'error');
      return;
    }
    formattedDate = format(parsedDate, 'yyyy-MM-dd');
  } else {
    showAlert('ERROR', 'Invalid date value', 'error');
    return;
  }

  // Validate that the date is not in the past using date-fns
  const estShippingDate = parseISO(formattedDate);
  const today = startOfDay(new Date());

  if (isBefore(estShippingDate, today)) {
    showAlert('ERROR', 'Estimated shipping date cannot be in the past.', 'error');
    return;
  }

  shipping_dates.value = [...shipping_dates.value, formattedDate];

  let url = `customer-orders/${props.orderItem.id}/est-shipping-date`;

  try {
    await http.post(url, { shipping_dates: shipping_dates.value });

    if (notify) {
      let url = `notify-customer-shipping-date/${props.orderItem.id}/order/${props.order.id}`;
      if (formattedDate) {
        await http.post(url);
        showAlert('Success', 'Order put in production and customer notified', 'success');
      } else {
        showAlert('error', 'Please save shipping date.', 'error');
      }
    }

    est_shipping_date.value = null;
    showAlert('Success', 'Order put in production', 'success');
    emit('putInProductionSaved', notify);
    closeEvent();
  } catch (errorResponse) {
    showAlert('ERROR', 'Failed to put order in production', 'error');
  }
};

const saveShippingDetails = async () => {
  if (!shipping_company.value) {
    showToast('Please select a shipping company', 'error');
    return;
  }

  if (!tracking_no.value || tracking_no.value.trim().length === 0 ) {
    showToast('Please enter a tracking number', 'error');
    return;
  }

  if (!tracking_link.value || tracking_link.value.trim().length === 0) {
    showToast('Please enter a tracking link', 'error');
    return;
  }

  try {
    const shippingData = {
      shipping_company: shipping_company.value,
      tracking_no: tracking_no.value,
      tracking_link: tracking_link.value || '',
    };

    // Emit the shipping details to parent component
    // The parent component will handle the makeActivityData call with ORDERSHIPPED status
    emit('shippingDetailsSaved', shippingData);

    // Reset form
    shipping_company.value = '';
    tracking_no.value = '';
    tracking_link.value = '';

    showAlert('Success', 'Shipping details saved successfully', 'success');
    closeEvent();
  } catch (errorResponse) {
    showAlert('ERROR', 'Failed to save shipping details', 'error');
  }
};
</script>
<style lang="sass" scoped></style>
